import React, { useEffect } from 'react'
import { StyleSheetManager } from 'styled-components'
import tw, { styled, theme } from 'twin.macro'
import Head from 'next/head'
import EditorLayout from './EditorLayout'
import HeaderContainer from './header'
import TextInput from './text-input'
import ImageInput from './image-input'
// import Location from './location'
import Category, { CategoryCalloutRoot } from './category'
import Account from './account'
import PublishSettingButton from './publish-setting'
import SocialMediaSettings from './publish-setting/social-media-settings'
import PreviewButton from './preview'
import { ArticleProvider } from './_core'
import { Provider as ReakitProvider } from 'reakit'
import { GetServerSideProps } from 'next'
import { getEditingArticle } from './_core/getEditingArticle'
import { getApolloClient } from '@libs/getApolloClient'
import { EditingArticle_editingArticle } from '@apollo-types/EditingArticle'
import { getCategories } from '@libs/categories/getCategories'
import { EditorPageContextProvider, Categories } from './_core/EditorPageContext'
import { withRNApp } from '@libs/RNApp'
import { useSelector } from 'react-redux'

export const getServerSideProps: GetServerSideProps = async (context) => {
  const client = getApolloClient(context)
  const [result, categories] = await Promise.all([
    getEditingArticle(client, context?.params?.id as string),
    getCategories(client),
  ])

  const pageProps: any = { categories }

  if (result?.data) pageProps.editingArticle = result.data
  if (result?.error) pageProps.error = result.error

  return { props: pageProps }
}

const AsideContainer = () => {
  return (
    <>
      <Section tw="hidden md:block">
        <Account />
      </Section>
      <Section>
        <Category />
      </Section>
      <CategoryCalloutRoot id="categoryCalloutRoot" />
      {/* <Section>
        <Location />
      </Section> */}
      <MobileSection>
        <SocialMediaSettings />
      </MobileSection>
      <ButtonSection>
        <PublishSettingButton />
        <PreviewButton />
      </ButtonSection>
    </>
  )
}

const MainContainer = () => {
  return (
    <>
      <ImageInput />
      <TextInput />
    </>
  )
}

interface EditorPageProps {
  editingArticle?: Partial<EditingArticle_editingArticle>
  categories: Categories
  error?: {
    message: string
  }
  isApp: boolean
}

function EditorPage({ isApp, error, editingArticle, ...pageProps }: EditorPageProps) {
  const username = useSelector<any, any>((state) => state.currentUser.getIn(['userInfo', 'name']))

  useEffect(() => {
    if (!username) {
      // TODO: 加上 i18n 但 i18next SSR 用不出來先作罷，打算來逐步替換 i18n 套件
      window.alert('建立文章前請先設定個人暱稱')
      window.location.href = '/user/editor'

      return
    }

    if (!error) return
    window.alert(error.message)
  }, [error])

  return (
    <>
      <Head>
        <link rel="stylesheet" href="https://unpkg.com/leaflet@1.0.3/dist/leaflet.css" />
        <title key="title">編輯文章｜PopDaily 波波黛莉</title>
      </Head>
      <StyleSheetManager disableVendorPrefixes>
        <ReakitProvider>
          <EditorPageContextProvider value={pageProps}>
            <ArticleProvider editingArticle={editingArticle}>
              <EditorLayout
                header={!isApp ? <HeaderContainer /> : null}
                main={<MainContainer />}
                aside={<AsideContainer />}
              />
            </ArticleProvider>
          </EditorPageContextProvider>
        </ReakitProvider>
      </StyleSheetManager>
    </>
  )
}

export default React.memo(withRNApp(EditorPage))

const Section = styled.section`
  ${tw`flex flex-col md:border border-t border-solid border-gray-30 md:rounded-md`};
  padding: 20px 16px;
  gap: 8px; /* 為了讓 ButtonSection 中的 Lightbox 不要有 margin */

  @media (min-width: ${theme`screens.md`}) {
    padding: 16px;
  }
`

const ButtonSection = styled(Section)`
  ${tw`m-0 md:p-0 md:border-0`};

  @media (min-width: ${theme`screens.md`}) {
    order: -1;
    margin-bottom: 16px;
  }
`

const MobileSection = styled(Section)`
  ${tw`block md:hidden`}
`
